import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { MDBIcon, MDBBtn, MDBCollapse } from 'mdb-react-ui-kit'
import CustomAlert from './../../shared/CustomAlert'
import { UIInput, UITextarea, UIInputToggleSwitch } from './../../forms/form-fields'
import Store, { TITLE_MAX, BODY_MAX, TITLE_VISIBLE, BODY_VISIBLE } from './AgentPushMessage.store'
import { describeDeviceStatus } from './../pushCopy'

import './AgentPushMessage.component.scss'

const READINESS_ICON = {
  verified: 'check-circle',
  unverified: 'question-circle',
  'test-failed': 'exclamation-triangle',
  ios: 'mobile-alt',
  blocked: 'ban',
  none: 'bell-slash',
  unknown: 'question-circle',
  loading: 'spinner',
}

const formatWhen = value => {
  if (!value) return ''
  let d = new Date(value)
  if (isNaN(d.getTime())) d = new Date(`${value}`.replace(' ', 'T'))
  if (isNaN(d.getTime())) return `${value}`
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/**
 * BST → Agent messaging, on the agent's profile.
 *
 * Role-gated by the caller in AccountProfile.jsx with ignoreAssumed=true, so the
 * check reads the real logged-in admin rather than the agent being viewed.
 *
 * Works from both routes to this page. "Assume" (ManageAgentsPage:279) signs in
 * as the agent, so requests carry the AGENT's token; the send endpoint resolves
 * the real sender from the assume_id -> user_assumes.parent_user_id, server
 * side. See functions/handlers/rest/push-messages.js.
 *
 * Layout follows the order the questions actually get asked:
 *   who is this going to and will it reach them → what does it say →
 *   how should it behave → send.
 */
const AgentPushMessage = ({ agentId, agentName, agentEmail }) => {
  const [showDevices, setShowDevices] = useState(false)

  useEffect(() => {
    if (agentId) Store.init(agentId)
  }, [agentId])

  const readiness = Store.readiness
  const { form } = Store
  const devices = Store.devices || []

  // From the caller's loaded agent record, NOT UserProfileService. That reads
  // child_details || user_details, which is the agent only while assuming — on
  // /:id/account-profile it returns the ADMIN, so the card and the confirm
  // dialog named the sender as the recipient beside the agent's real id.
  // The confirm step exists to let a human check who they are about to message;
  // showing the wrong person there defeats the whole point of it.
  const recipientName = `${agentName || ''}`.trim() || `Agent #${Store.agentId}`
  const recipientEmail = `${agentEmail || ''}`.trim()

  /**
   * Quiet until it matters. A running commentary on every keystroke reads as an
   * error state; the number alone is enough until the text actually risks being
   * truncated, and only then does it earn colour.
   */
  const counter = (used, visible, max) => {
    const over = used > max
    const past = used > visible
    return (
      <span
        className={`push-counter ${over ? 'is-over' : past ? 'is-warn' : ''}`}
        title={over ? `Over the ${max} character limit` : `Most devices show about ${visible} characters`}
      >
        {used}
        <span className='push-counter-sep'>/</span>
        {over ? max : visible}
        {past && !over ? ' · may be cut off' : ''}
        {over ? ' · too long' : ''}
      </span>
    )
  }

  const renderPreview = () => (
    <figure className='push-preview-wrap'>
      <figcaption>How it will look on their device</figcaption>
      <div className='push-preview' aria-hidden='true'>
        <div className='push-preview-chrome'>
          <span>USABG</span>
          <span>now</span>
        </div>
        <div className='push-preview-title'>{form.title || 'Message title'}</div>
        <div className='push-preview-body'>{form.body || 'Your message will appear here.'}</div>
      </div>
    </figure>
  )

  const renderConfirm = () => (
    <div className='push-confirm' role='group' aria-label='Confirm send'>
      <p className='push-confirm-lead'>
        <MDBIcon icon='exclamation-triangle' /> Sends immediately — this cannot be recalled.
      </p>
      <dl>
        <dt>To</dt>
        <dd>
          {recipientName}
          <span className='push-muted'>
            {' '}
            · #{Store.agentId} · {recipientEmail}
          </span>
        </dd>
        <dt>Reaches</dt>
        <dd>{Store.deliverySummary}</dd>
        <dt>Title</dt>
        <dd>{form.title}</dd>
        <dt>Message</dt>
        <dd>{form.body}</dd>
        {form.link_url ? (
          <>
            <dt>Opens</dt>
            <dd>{form.link_url}</dd>
          </>
        ) : null}
        {form.severity === 'critical' ? (
          <>
            <dt>Priority</dt>
            <dd>Critical — stays on screen and shows a banner until acknowledged</dd>
          </>
        ) : null}
      </dl>
      <div className='push-actions'>
        <MDBBtn color='indigo' disabled={Store.isSending} onClick={() => Store.send()}>
          {Store.isSending ? 'Sending…' : 'Send Now'}
        </MDBBtn>
        <button
          type='button'
          className='push-link-btn'
          disabled={Store.isSending}
          onClick={() => Store.cancelConfirm()}
        >
          Back to editing
        </button>
      </div>
    </div>
  )

  const renderForm = () => (
    <>
      <div className='push-compose'>
        <div className='push-fields'>
          <div className='push-field'>
            <UIInput
              name='title'
              type='text'
              label='Title'
              required
              value={form.title}
              showValidity={Store.showValidity}
              disabled={Store.isSending}
              onChange={e => Store.setField('title', e.target.value)}
            />
            {counter((form.title || '').length, TITLE_VISIBLE, TITLE_MAX)}
          </div>

          <div className='push-field'>
            <UITextarea
              name='body'
              label='Message'
              required
              value={form.body}
              showValidity={Store.showValidity}
              disabled={Store.isSending}
              onChange={e => Store.setField('body', e.target.value)}
            />
            {counter((form.body || '').length, BODY_VISIBLE, BODY_MAX)}
            <span className='push-hint'>Plain text only — notifications cannot show formatting or links.</span>
          </div>
        </div>

        {renderPreview()}
      </div>

      <div className='push-options'>
        <div className='push-option'>
          <UIInputToggleSwitch
            id='push-severity'
            name='severity'
            width={140}
            labelOn='Critical'
            labelOff='Standard'
            disabled={Store.isSending}
            value={form.severity === 'critical'}
            onChange={e => Store.setField('severity', e.target.value ? 'critical' : 'standard')}
          />
          <span className='push-option-hint'>
            {form.severity === 'critical'
              ? 'Stays on screen, and shows a banner in the back office until they acknowledge it.'
              : 'Behaves like a normal notification.'}
          </span>
        </div>

        <div className='push-option'>
          {/* Hidden while loading: the default depends on the devices, and the
              control must not change value in front of the admin. */}
          {Store.isLoadingDevices ? (
            <span className='push-option-hint'>Checking their devices…</span>
          ) : (
            <>
              <UIInputToggleSwitch
                id='push-email'
                name='send_email'
                width={140}
                labelOn='Email too'
                labelOff='No email'
                disabled={Store.isSending}
                value={!!form.send_email}
                onChange={e => Store.setField('send_email', !!e.target.value)}
              />
              <span className='push-option-hint'>
                {Store.shouldFallbackToEmail
                  ? 'Recommended — push is not confirmed working for this agent.'
                  : 'Also send this as an email.'}
              </span>
            </>
          )}
        </div>

        <div className='push-field push-field--link'>
          <UIInput
            name='link_url'
            type='text'
            label='Open a page when tapped'
            value={form.link_url}
            showValidity={Store.showValidity}
            disabled={Store.isSending}
            onChange={e => Store.setField('link_url', e.target.value)}
            onBlur={() => Store.normaliseLinkField()}
          />
          {Store.linkError ? (
            <span className='push-hint push-hint--error' role='alert'>
              {Store.linkError}
            </span>
          ) : (
            <span className='push-hint'>
              Optional. A back office path such as <code>/account-billinginfo</code>. Paste a full USABG link and it
              will be shortened for you. Defaults to their Alerts page.
            </span>
          )}
        </div>
      </div>

      {/* The button used to just do nothing when validation blocked it, with no
          way to discover why. Say it out loud instead. */}
      {Store.showValidity && Store.blockedReason ? (
        <p className='push-blocked' role='alert'>
          <MDBIcon icon='exclamation-circle' /> {Store.blockedReason}
        </p>
      ) : null}

      <div className='push-actions push-actions--primary'>
        <MDBBtn color='indigo' disabled={Store.isSending} onClick={() => Store.openConfirm()}>
          Review &amp; Send
        </MDBBtn>
        <button
          type='button'
          className='push-link-btn'
          disabled={Store.isSending}
          onClick={() => Store.sendTestToSelf()}
          title='Sends this exact message to your own account instead'
        >
          Send a test to myself first
        </button>
      </div>
    </>
  )

  return (
    <div id='AgentPushMessageComponent'>
      {/* Who + can they receive it, answered together and first. */}
      <header className='push-header'>
        <div className='push-to'>
          <span className='push-to-label'>To</span>
          <strong>{recipientName}</strong>
          <span className='push-muted'>
            #{Store.agentId} · {recipientEmail}
          </span>
        </div>

        <div className={`push-readiness push-readiness--${readiness.level}`}>
          <MDBIcon icon={READINESS_ICON[readiness.level] || 'question-circle'} />
          <span>
            {readiness.label}
            {readiness.detail ? <em className='push-readiness-detail'>{readiness.detail}</em> : null}
          </span>
        </div>

        <p className='push-summary'>
          <MDBIcon icon='paper-plane' /> This message reaches them via <strong>{Store.deliverySummary}</strong>.
          {devices.length ? (
            <button
              type='button'
              className='push-link-btn push-link-btn--inline'
              aria-expanded={showDevices}
              onClick={() => setShowDevices(!showDevices)}
            >
              {/* The count beside "push" is live devices only; this one is
                  every row. Same noun for two different populations read as a
                  contradiction ("push (2 devices) … View 5 devices"), so name
                  what a row actually is: one browser on one device. */}
              {showDevices ? 'Hide devices & browsers' : `View all ${devices.length} devices & browsers`}
            </button>
          ) : null}
        </p>

        <MDBCollapse open={showDevices}>
          <p className='push-device-list-note'>
            One row per browser on each device — the same computer appears once for each browser signed in. Only rows
            marked "Receiving notifications" can receive a push.
          </p>
          <ul className='push-device-list'>
            {devices.map(d => (
              <li key={`apm-device-${d.id}`}>
                <span>{d.label || 'Unknown device'}</span>
                <span className='push-muted'>
                  {describeDeviceStatus(d)} · {formatWhen(d.last_seen_at)}
                </span>
              </li>
            ))}
          </ul>
        </MDBCollapse>
      </header>

      {Store.outcome ? (
        <CustomAlert
          color={
            Store.outcome.status === 'success' ? 'success' : Store.outcome.status === 'mismatch' ? 'danger' : 'warning'
          }
          className='push-outcome'
          role='alert'
        >
          <strong>{Store.outcome.message}</strong>
          {Store.outcome.detail ? <div className='push-muted'>{Store.outcome.detail}</div> : null}
        </CustomAlert>
      ) : null}

      {Store.isConfirming ? renderConfirm() : renderForm()}

      {Store.history && Store.history.length ? (
        <div className='push-history'>
          <h6>Recent messages to this agent</h6>
          <ul>
            {Store.history.map(m => (
              <li key={`apm-history-${m.id}`}>
                <div className='push-history-head'>
                  <strong>{m.title}</strong>
                  <span className='push-muted'>{formatWhen(m.created_at)}</span>
                </div>
                <div className='push-history-body'>{m.body}</div>
                <div className='push-muted'>
                  {parseInt(m.devices_delivered)}/{parseInt(m.devices_targeted)} device
                  {parseInt(m.devices_targeted) === 1 ? '' : 's'}
                  {parseInt(m.emailed) ? ' · emailed' : ''}
                  {`${m.severity}` === 'critical' ? ' · critical' : ''}
                  {m.clicked_at ? ` · opened ${formatWhen(m.clicked_at)}` : ''}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

export default observer(AgentPushMessage)
